import React, { useEffect, useState } from 'react';
import { AppContext } from '../../App';

import { dataFetcher } from '../../Utils/dataFetcher';
import { AthleteSelector } from '../AthleteSelector';
import { CountryFlag } from '../../Utils/flags';
import { secondsInPast } from '../../Utils/time';

import { motion } from 'framer-motion';

import { convertTime } from './utils';
import { CenterCol } from './centerCol';

import style from './head-to-head.module.scss';
import TagManager from 'react-gtm-module';
import { NoData } from '../NoData';
import { getTrans } from '../../Utils/translate';
import { capitalize } from '../../Utils/string';
import { CosmosImage, CosmosText } from '../../cosmos/web-scoped/react';
import athlete_skeleton from '../AthleteProfileLite/athlete_skeleton.png';

// animation settings
const variants = {
  open: (custom) => ({
    opacity: 1,
    x: 0,
    transition: {
      delay: custom.index && custom.index ? custom.index * 0.05 : 0.05
    }
  }),
  closed: (custom) => ({
    opacity: 0,
    x: custom.direction && custom.direction === 'right' ? 100 : -100
  })
};

const HeadToHead = ({ config }) => {
  const context = React.useContext(AppContext);
  const uriConfig = context.config.uriConfig;

  const [athletes, setAthletes] = useState(undefined);

  const [dynamicAthletes, setDynamicAthletes] = useState(undefined);
  const [options, setOptions] = useState(undefined);
  const [activeAthlete, setActiveAthlete] = useState(undefined);

  const [zoneAthletes, setZoneAthletes] = useState(undefined);
  const [athleteRightId, setAthleteRightId] = useState(undefined);
  const [athleteLeftId, setAthleteLeftId] = useState(undefined);

  const [showTiming, setShowTiming] = useState(false);
  const [currentLap, setCurrentLap] = useState(1);
  const [zoneData, setZoneData] = useState(undefined);

  const lang = context.lang;

  /** LIVE DATA start */
  // const [leftData, setLeftData] = useState(undefined);
  // const [rightData, setRightData] = useState(undefined);
  // const [numberOfZones, setNumberOfZones] = useState(0);
  const [liveData, setLiveData] = useState({
    numberOfZones: 0,
    leftData: undefined,
    rightData: undefined
  });

  const fetchData = async () => {
    Promise.all([
      dataFetcher(config.uriResults, uriConfig),
      dataFetcher(config.uriList, uriConfig)
    ])
      .then((data) => {
        let athletesWithResults = [];
        data[1].entrants.map((entrant) => {
          const ID = entrant.athlete.id;
          const athleteObject = { ...entrant };
          let result;
          if (data[0] && data[0].results) {
            result = data[0].results.find(({ athlete }) => athlete.id === ID);
          }
          athleteObject.rank = result ? result.rank : undefined;
          athletesWithResults.push(athleteObject);
          return false;
        });
        setAthletes(athletesWithResults);
        setAthleteLeftId('1');
        setAthleteRightId('2');

        if (config.uriActive) {
          dataFetcher(config.uriActive, uriConfig).then((data) => {
            //Check if active id is not a spoiler
            if (data.recent_finishers && data.recent_finishers.length >= 1) {
              const recentFinishers = data.recent_finishers;
              const normalActiveId = data.active[0].athlete_id;
              const activeInFinish = recentFinishers.filter(
                (finisher) =>
                  finisher.id === normalActiveId &&
                  secondsInPast(
                    new Date(finisher.result_time),
                    config.delayDataSeconds || 60
                  )
              );
              if (activeInFinish && activeInFinish.length >= 1) {
                setActiveAthlete(data.active ? activeInFinish[0].id : undefined);
              } else {
                setActiveAthlete(data.active ? normalActiveId : undefined);
              }
            } else {
              setActiveAthlete(data.active ? data.active[0].athlete_id : undefined);
            }
            setAthleteLeftId(data.active ? 'active' : '1');
            setAthleteRightId(data.active ? '1' : '2');
          });
        }
      })
      .catch(console.error);

    if (config.zoneDetail) {
      await dataFetcher(config.zoneDetail, uriConfig).then((data) => {
        if (data && data.track && data.track.mtb && data.track.mtb.lap_zones) {
          setZoneData(data.track.mtb.lap_zones);
        }
      });
    }
  };

  useEffect(() => {
    fetchData();
    const dataLayerArgs = {
      dataLayer: {
        event: 'rbVideoTracking',
        eventCategory: 'video',
        eventAction: 'sidebar',
        eventLabel: `loaded`,
        widgetType: `${
          config.comparisionType === 'lap'
            ? `Lap Comparison - ${config.trackingTitle || config.navigationTitle}`
            : `Zone Analysis - ${config.trackingTitle || config.navigationTitle}`
        }`
      }
    };
    TagManager.dataLayer(dataLayerArgs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.comparisionType]);

  const handleChange = (e) => {
    const leftId = e[0].value;
    const rightId = e[1].value;

    let athleteObj = { first_name: '', last_name: '' };

    if (e.length === 2) {
      if (leftId !== athleteLeftId) {
        if (leftId <= 3 && leftId !== 'active') {
          athleteObj = athletes.find(
            ({ rank }) => parseInt(rank) === parseInt(leftId)
          ).athlete;
        } else if (leftId === 'leader') {
          athleteObj = athletes.find(
            ({ athlete_id }) => parseInt(athlete_id) === 1
          ).athlete;
        } else if (leftId === 'active') {
          athleteObj = athletes.find(({ athlete }) => athlete.id === activeAthlete);
        } else {
          athleteObj = athletes.find(({ athlete }) => athlete.id === leftId).athlete;
        }
      }
      if (rightId !== athleteRightId) {
        if (rightId <= 3 && rightId !== 'active') {
          athleteObj = athletes.find(
            ({ rank }) => parseInt(rank) === parseInt(rightId)
          ).athlete;
        } else if (rightId === 'leader') {
          athleteObj = athletes.find(
            ({ athlete_id }) => parseInt(athlete_id) === 1
          ).athlete;
        } else if (rightId === 'active') {
          athleteObj = athletes.find(({ athlete }) => athlete.id === activeAthlete);
        } else {
          athleteObj = athletes.find(({ athlete }) => athlete.id === rightId).athlete;
        }
      }

      const dataLayerArgs = {
        dataLayer: {
          event: 'rbVideoTracking',
          eventCategory: 'video',
          eventAction: 'sidebar',
          eventLabel: `${
            config.comparisionType === 'lap' ? 'Lap Comparison' : 'Zone Analysis'
          }::${athleteObj.first_name} ${athleteObj.last_name}`,
          widgetType: `${
            config.comparisionType === 'lap'
              ? `Lap Comparison - ${config.trackingTitle || config.navigationTitle}`
              : `Zone Analysis - ${config.trackingTitle || config.navigationTitle}`
          }`
        }
      };

      TagManager.dataLayer(dataLayerArgs);

      setAthleteLeftId(leftId);
      setAthleteRightId(rightId);
      setShowTiming(false);
    }
  };

  useEffect(() => {
    const getDynamicAthletes = (athletes) => {
      let dynamics = [];
      if (athletes) {
        athletes.forEach((athlete) => {
          if (activeAthlete && athlete.athlete.id === activeAthlete) {
            const dynamicObject = {
              ...athlete,
              dynamic: 'Current on Track',
              dynamicValue: 'active'
            };
            dynamics.push(dynamicObject);
          }
          if (athlete.athlete_id && athlete.athlete_id === '1' && !activeAthlete) {
            const dynamicObject = {
              ...athlete,
              dynamic: 'World Cup Leader',
              dynamicValue: 'leader'
            };
            dynamics.push(dynamicObject);
          }
          if (athlete.rank && athlete.rank > 0 && athlete.rank <= 3) {
            const dynamicObject = {
              ...athlete,
              dynamic:
                athlete.rank === 1 || athlete.result.rank === 1
                  ? activeAthlete
                    ? 'Hotseat 1'
                    : 'Current Leader'
                  : athlete.rank === 2 || athlete.result.rank === 2
                  ? activeAthlete
                    ? 'Hotseat 2'
                    : 'Current Second'
                  : activeAthlete
                  ? 'Hotseat 3'
                  : 'Current Third',
              dynamicValue: athlete.rank
            };
            dynamics.push(dynamicObject);
          }
        });
      }
      return dynamics;
    };
    const da = getDynamicAthletes(athletes);
    setDynamicAthletes(da.length > 0 ? da : undefined);
  }, [athletes, activeAthlete]);

  useEffect(() => {
    let opt;
    if (dynamicAthletes && dynamicAthletes.length > 0) {
      opt = athletes.concat(dynamicAthletes);
    } else if (athletes) {
      opt = athletes;
    }
    setOptions(opt);
  }, [athletes, dynamicAthletes]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  useEffect(() => {
    // console.log(config.comparisionType);
    // console.log(athleteRightId);

    if (athleteLeftId && athleteRightId && athleteLeftId !== athleteRightId) {
      let leftId = athleteLeftId;
      let rightId = athleteRightId;

      if (leftId <= 3 && leftId !== 'active') {
        let athlete = athletes.find(({ rank }) => parseInt(rank) === parseInt(leftId));

        if (!athlete) {
          leftId = [
            ...athletes.sort((a, b) => {
              return `${a.athlete.last_name.toLowerCase()} ${a.athlete.first_name.toLowerCase()}` <
                `${b.athlete.last_name.toLowerCase()} ${b.athlete.first_name.toLowerCase()}`
                ? -1
                : `${a.athlete.last_name.toLowerCase()} ${a.athlete.first_name.toLowerCase()}` >
                  `${b.athlete.last_name.toLowerCase()} ${b.athlete.first_name.toLowerCase()}`
                ? 1
                : 0;
            })
          ][0].athlete.id;
        } else {
          leftId = athletes.find(({ rank }) => parseInt(rank) === parseInt(leftId))
            .athlete.id;
        }
      }
      if (rightId <= 3 && rightId !== 'active') {
        let athlete = athletes.find(({ rank }) => parseInt(rank) === parseInt(rightId));

        if (!athlete) {
          rightId = athletes[1].athlete.id;
        } else {
          rightId = athletes.find(({ rank }) => parseInt(rank) === parseInt(rightId))
            .athlete.id;
        }
      }

      if (leftId === 'leader') {
        leftId = athletes.find(({ athlete_id }) => parseInt(athlete_id) === 1).athlete.id;
      }
      if (rightId === 'leader') {
        rightId = athletes.find(({ athlete_id }) => parseInt(athlete_id) === 1).athlete
          .id;
      }

      if (leftId === 'active') {
        leftId = activeAthlete;
      }
      if (rightId === 'active') {
        rightId = activeAthlete;
      }

      const uri = config.uriDetail
        .replace('{idleft}', leftId)
        .replace('{idright}', rightId);
      let lapuri;
      if (config.laps > 1) {
        lapuri = uri.replace('{lap}', currentLap);
      }

      dataFetcher(lapuri ? lapuri : uri, uriConfig).then((data) => {
        if (typeof data.error !== 'undefined') {
          setLiveData({
            numberOfZones: 0,
            leftData: [],
            rightData: []
          });

          return;
        }
        let currentNumberOfZones = 0;
        if (data && data.athletes) {
          if (data.zones) {
            currentNumberOfZones = data.zones.length;
          }
          setZoneAthletes(data.athletes);
          if (Array.isArray(data.zones)) {
            let leftAthlete = [];
            let rightAthlete = [];

            data.zones
              .sort((a, b) =>
                !a.zone_index
                  ? 1
                  : !b.zone_index || a.zone_index < b.zone_index
                  ? -1
                  : a.zone_index > b.zone_index
                  ? 1
                  : 0
              )
              .forEach((zone) => {
                zone.zone_comparison.forEach((zoneResult) => {
                  if (zoneResult.comparison_ref === 1) leftAthlete.push(zoneResult);
                  if (zoneResult.comparison_ref === 2) rightAthlete.push(zoneResult);
                });
              });
            // setLeftData(leftAthlete);
            // setRightData(rightAthlete);

            setLiveData({
              numberOfZones: currentNumberOfZones,
              leftData: [...leftAthlete],
              rightData: [...rightAthlete]
            });
          }
        }
      });
    }
  }, [
    config,
    athleteRightId,
    athleteLeftId,
    currentLap,
    activeAthlete,
    athletes,
    uriConfig
  ]);

  const benchmarkStyle = (time, fixed, hidezeros, nogrey) => {
    if (time) {
      let gap = time;
      const minus = time.includes('-');
      if (minus) {
        gap = gap.replaceAll('-', '');
      }
      let reftime = convertTime(gap, fixed || 1);
      if (hidezeros) {
        reftime = reftime.replace('0:0', '');
        reftime = reftime.replace('0:', '');
      }

      const zero =
        reftime === '0:00.0' || reftime === '0:00.00' || reftime === '0:00.000';
      const sign = minus ? '-' : '+';
      return (
        <span
          className={`font-mono ${
            reftime < 1 && !nogrey && config.comparisionType === 'lap'
              ? 'text-grey'
              : minus
              ? 'text-green'
              : zero
              ? ''
              : 'text-red'
          }`}
        >
          {zero ? <span className={style.hiddenSign}>+</span> : sign} {reftime}
        </span>
      );
    } else {
      return false;
    }
  };

  const tagLapChange = (newLapNum) => {
    const dataLayerArgs = {
      dataLayer: {
        event: 'rbVideoTracking',
        eventCategory: 'video',
        eventAction: 'sidebar',
        eventLabel: `LAP ${newLapNum}`,
        widgetType: `${
          config.comparisionType === 'lap'
            ? `Lap Comparison - ${config.trackingTitle || config.navigationTitle}`
            : `Zone Analysis - ${config.trackingTitle || config.navigationTitle}`
        }`
      }
    };

    TagManager.dataLayer(dataLayerArgs);
  };

  let lapComparision;
  if (config.comparisionType) {
    lapComparision = config.comparisionType === 'lap';
  }

  return (
    <div className={lapComparision ? style.lapComparision : style.zoneComparison}>
      {options && liveData.leftData && liveData.rightData && (
        <>
          <AthleteSelector
            className={`${style.selector}`}
            comparison
            description
            filterVals={config.selectFilter}
            options={options
              .sort((a, b) => {
                if (a.dynamicValue !== undefined || b.dynamicValue !== undefined) {
                  return a.dynamicValue === undefined
                    ? 1
                    : a.dynamicValue === 'leader' && b.dynamicValue === undefined
                    ? 0
                    : a.dynamicValue === 1 || b.dynamicValue === undefined
                    ? -1
                    : a.dynamicValue === 'active' &&
                      b.dynamicValue !== undefined &&
                      b.dynamicValue !== 1
                    ? -1
                    : b.dynamicValue === 'active' || a.dynamicValue === 'leader'
                    ? 1
                    : parseInt(a.dynamicValue) < parseInt(b.dynamicValue)
                    ? -1
                    : 1;
                }

                return `${a.athlete.last_name.toLowerCase()} ${a.athlete.first_name.toLowerCase()}` <
                  `${b.athlete.last_name.toLowerCase()} ${b.athlete.first_name.toLowerCase()}`
                  ? -1
                  : `${a.athlete.last_name.toLowerCase()} ${a.athlete.first_name.toLowerCase()}` >
                    `${b.athlete.last_name.toLowerCase()} ${b.athlete.first_name.toLowerCase()}`
                  ? 1
                  : 0;
              })
              .map(({ athlete_id, athlete, dynamic, dynamicValue }) => {
                const athleteData = athlete.hasOwnProperty('athlete') ? athlete.athlete : athlete;
                return {
                  label: dynamic ? (
                    <>{dynamic}</>
                  ) : (
                    <>
                      {(config.showAthleteImage || config.showSelectAthleteImage) && (
                          <CosmosImage
                            class="option-image"
                            image={athleteData.image ? (athleteData.image.profile || athlete_skeleton) : athlete_skeleton}
                            image-alt={`image-${athleteData.first_name}-${athleteData.last_name}`}/>
                        )}
                      <CosmosText
                        class="option-text"
                        kind="subtle">
                        <img
                          className={`flag`}
                          src={CountryFlag(athleteData.nationality.iso3)}
                          alt={athleteData.nationality.iso3}
                        />
                        {`${capitalize(athleteData.first_name)} ${capitalize(athleteData.last_name)}`}
                      </CosmosText>
                    </>
                  ),
                  descriptionLabel: dynamic ? dynamic : false,
                  selectedLabel: (
                    <>
                      <img
                        className={`flag`}
                        src={CountryFlag(athleteData.nationality.iso3)}
                        alt={athleteData.nationality.iso3}
                      />
                      {`${capitalize(athleteData.first_name.substring(0, 1))}. ${capitalize(athleteData.last_name)}`}
                    </>
                  ),
                  value: dynamicValue ? dynamicValue : athleteData.id,
                  rank: dynamic ? 1 : parseInt(athlete_id)
                };
              })}
            onChange={handleChange}
            value={athleteLeftId}
            comparisonValue={athleteRightId}
          />
          {config.laps > 1 && (
            <div className={'pagination'}>
              <span
                className={'paginationArrowWrapper'}
                onClick={() => {
                  const newLapNum = currentLap >= 2 ? currentLap - 1 : 1;
                  tagLapChange(newLapNum);
                  setCurrentLap(newLapNum);
                }}
                style={{
                  cursor: currentLap < 1 ? 'pointer' : 'default'
                }}>
                {currentLap > 1 && <span className={'paginationArrow'}></span>}
              </span>

              <span className={'paginationCurrent'}>
                {getTrans(lang.lap)} {currentLap}
              </span>
              <span
                className={'paginationArrowWrapper'}
                onClick={() => {
                  const newLapNum =
                    currentLap <= config.laps - 1 ? currentLap + 1 : config.laps;
                  tagLapChange(newLapNum);
                  setCurrentLap(newLapNum);
                }}
                style={{
                  cursor: currentLap < config.laps ? 'pointer' : 'default'
                }}
              >
                {currentLap < config.laps && <span className={'paginationArrow'}></span>}
              </span>
            </div>
          )}
          <div className={`${style.table} ${lapComparision ? style.lapComparision : ''}`}>
            <div className={`${style.timingCol} ${showTiming ? '' : style.stripesLeft}`}>
              {liveData.leftData &&
                liveData.leftData.map((data, index) => {
                  let time = convertTime(
                    data.zone_time,
                    config.comparisionType === 'zone' ? 3 : 1
                  );
                  if (index + 1 > liveData.numberOfZones) {
                    return false;
                  }
                  //hide zeros
                  if (time && config.comparisionType === 'lap') {
                    time = time.replace('0:0', '');
                    time = time.replace('0:', '');
                  }
                  return (
                    <motion.div
                      custom={{ index: index, direction: 'left' }}
                      initial={showTiming ? 'open' : 'closed'}
                      animate={
                        config.alwaysShowTime ? 'open' : showTiming ? 'open' : 'closed'
                      }
                      variants={variants}
                      className={`${style.time} ${style.timeLeft} font-mono ${
                        !config.colorIndicatorLeft ? style.fullBorderRadius : ''
                      }`}
                      key={index}>
                      {time}
                    </motion.div>
                  );
                })}
            </div>
            <div className={`${style.infoCol}`}>
              <CenterCol
                config={config}
                numberOfZones={liveData.numberOfZones}
                leftData={liveData.leftData}
                rightData={liveData.rightData}
                zoneData={zoneData}
              />
            </div>
            <div className={`${style.timingCol} ${showTiming ? '' : style.stripesRight}`}>
              {liveData.rightData &&
                liveData.rightData.map((data, index) => {
                  if (index + 1 > liveData.numberOfZones) {
                    return false;
                  }
                  return (
                    <motion.div
                      custom={{ index: index, direction: 'right' }}
                      initial={showTiming ? 'open' : 'closed'}
                      animate={
                        config.alwaysShowTime ? 'open' : showTiming ? 'open' : 'closed'
                      }
                      variants={variants}
                      className={`${style.time} ${style.timeRight} ${
                        !config.colorIndicatorRight ? style.fullBorderRadius : ''
                      }`}
                      key={index}>
                      {benchmarkStyle(
                        data.zone_comparison_gap,
                        config.comparisionType === 'zone' ? 3 : 1,
                        true
                      )}
                    </motion.div>
                  );
                })}
            </div>
          </div>
          {liveData.leftData.length ? (
            <div className={style.results}>
              <div className={`font-mono ${style.endTime} text-left`}>
                {zoneAthletes &&
                  convertTime(
                    zoneAthletes[0].time,
                    config.comparisionType === 'zone' ? 3 : 1
                  )}
                <br />
                {zoneAthletes &&
                  benchmarkStyle(
                    zoneAthletes[0].time_to_benchmark,
                    config.comparisionType === 'zone' ? 3 : 1,
                    true,
                    true
                  )}
              </div>
              <div className={style.places}>
                {config.comparisionType === 'zone' && (
                  <span className={`font-mono ${style.place}`}>
                    {zoneAthletes[0].rank}.
                  </span>
                )}
                {config.comparisionType === 'lap' && (
                  <span className={style.endTimeText}>{getTrans(lang.total)}</span>
                )}
                <span
                  className={`${style.buttonBar}`}
                  style={{
                    width: config.alwaysShowTime ? 74 : '',
                    display: config.alwaysShowTime ? 'none' : 'inline-block'
                  }}>
                  {!config.alwaysShowTime && (
                    <button
                      className={'btn-mini'}
                      onClick={() => {
                        const dataLayerArgs = {
                          dataLayer: {
                            event: 'rbVideoTracking',
                            eventCategory: 'video',
                            eventAction: 'sidebar',
                            eventLabel: `Timing::${!showTiming ? 'SHOW' : 'HIDE'}`,
                            widgetType: `${
                              config.comparisionType === 'lap'
                                ? `Lap Comparison - ${
                                    config.trackingTitle || config.navigationTitle
                                  }`
                                : `Zone Analysis - ${
                                    config.trackingTitle || config.navigationTitle
                                  }`
                            }`
                          }
                        };

                        TagManager.dataLayer(dataLayerArgs);
                        setShowTiming(!showTiming);
                      }}>
                      {!showTiming
                        ? getTrans(config.showAllButton)
                        : getTrans(config.showLessButton)}
                    </button>
                  )}
                </span>
                {config.comparisionType === 'zone' && (
                  <span className={`font-mono ${style.place}`}>
                    {zoneAthletes[1].rank}.
                  </span>
                )}
              </div>
              <div className={`font-mono ${style.endTime} text-right`}>
                {zoneAthletes &&
                  convertTime(
                    zoneAthletes[1].time,
                    config.comparisionType === 'zone' ? 3 : 1
                  )}
                <br />
                {zoneAthletes &&
                  benchmarkStyle(
                    zoneAthletes[1].time_to_benchmark,
                    config.comparisionType === 'zone' ? 3 : 1,
                    true,
                    true
                  )}
              </div>
            </div>
          ) : (
            <NoData />
          )}
        </>
      )}
      <div className="separator"></div>
    </div>
  );
};

export { HeadToHead };
